import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, RotateCcw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import type { RouterOutputs } from 'shared/api'
import { trpc, trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { Fact, Facts } from 'shared/components/facts'
import { PageFrame } from 'shared/components/page-frame'
import { Hint, Stat, Stats } from 'shared/components/section'
import { ToneNote, type Tone } from 'shared/components/tone-banner'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'shared/components/ui/sheet'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { WHOLE } from 'shared/lib/money'
import { formatDateTime, formatDayTime } from 'shared/lib/utils/date'

/**
 * The dead-letter queue — spec user story 65: a money-bearing fact that never
 * reached a partner within 24 hours is noticed by a person, not silently dropped.
 * Each row is an outbound domain event the drain gave up on after a day of
 * backoff.
 *
 * On top, whether there is a problem now: how many facts wait and since when. Below,
 * the queue as one table — the fact, the partner, the Parcel by its tracking code, the
 * attempts, when the drain gave up and why; «Повторить» on the row, one event at a
 * time, chosen by a person who looked at it. A click on a row opens its details — the
 * full error, the event's id and the fact as it would have gone.
 *
 * Ticket 32's supervised-cutover controls **deliberately reopen** ticket 11's
 * exclusion of "retry from the UI": without them the only recovery from a
 * misconfigured first launch is editing the database by hand. They are for one
 * moment, so they wait in the header's «⋯», in a dialog. There is no bulk replay
 * here, and no screen anywhere edits a stored payload, because a fact is not
 * corrected by rewriting it.
 */

type DeadLetter = RouterOutputs['outbox']['DeadLetters'][number]

/** What the last act here answered, in one of the three tones. */
type Outcome = { tone: Tone; text: string }

/**
 * The facts of the partner contract with a label (`outboundEventType.*`, the fact's
 * name with `_` for `.`). A row left from the retired v1 contract shows its raw name.
 */
const LABELLED_FACTS = new Set([
	'parcel.received_at_hub',
	'parcel.on_shelf',
	'parcel.return_accepted_at_point',
	'parcel.handed_over',
	'parcel.handover_cancelled',
	'parcel.left_shelf_for_return',
	'parcel.returned_to_partner',
	'parcel.disposed',
	'parcel.cancelled',
	'parcel.lost',
	'parcel.released_to_partner',
	'parcel.found',
	'parcel.paid_at_counter',
	'parcel.payment_cancelled',
	'parcel.edited_by_tezgo',
	'parcel.damaged',
	'parcel.storage_expired',
	'batch.closed_at_hub',
	'transit_box.closed',
])

function useFactName() {
	const { t } = useTranslate()
	return (type: string) =>
		LABELLED_FACTS.has(type) ? t(`outboundEventType.${type.replace(/\./g, '_')}` as never) : type
}

export function DeadLetters() {
	const { t } = useTranslate()
	const factName = useFactName()
	const query = useQuery(trpc.outbox.DeadLetters.queryOptions({}))
	const [outcome, setOutcome] = useState<Outcome | null>(null)
	const [busy, setBusy] = useState(false)
	const [openId, setOpenId] = useState<string | null>(null)
	const [cutover, setCutover] = useState(false)

	const rows = useMemo(() => query.data ?? [], [query.data])
	const open = rows.find((r) => r.eventId === openId) ?? null
	const partners = new Set(rows.map((r) => r.partnerId)).size
	// Newest first from the server: the last row has waited longest.
	const oldest = rows.length ? rows[rows.length - 1]!.deadLetteredAt : null

	const onReplay = async (eventId: string) => {
		setBusy(true)
		try {
			const res = await trpcClient.outbox.Replay.mutate({ eventId })
			setOutcome(
				res.outcome === 'replayed'
					? { tone: 'ready', text: t('deadLetters.replayDone') }
					: {
							tone: 'attention',
							text: t(res.outcome === 'not_dead_lettered' ? 'deadLetters.replayNotDead' : 'deadLetters.replayNotFound'),
						},
			)
			setOpenId(null)
		} catch {
			setOutcome({ tone: 'refused', text: t('deadLetters.failed') })
		} finally {
			await query.refetch()
			setBusy(false)
		}
	}

	const columns: ColumnDef<DeadLetter>[] = [
		{
			id: 'fact',
			header: t('deadLetters.colEvent'),
			cell: ({ row }) => <span className='font-medium'>{factName(row.original.type)}</span>,
		},
		{ id: 'partner', header: t('deadLetters.colPartner'), cell: ({ row }) => row.original.partnerName },
		{
			id: 'parcel',
			header: t('deadLetters.colParcel'),
			cell: ({ row }) =>
				row.original.trackingCode ? (
					<span className='font-mono'>{row.original.trackingCode}</span>
				) : (
					<span className='text-muted-foreground'>—</span>
				),
		},
		{
			id: 'attempts',
			header: t('deadLetters.colAttempts'),
			meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
			cell: ({ row }) => row.original.attempts,
		},
		{
			id: 'deadLettered',
			header: t('deadLetters.colDeadLettered'),
			meta: { cellClassName: 'whitespace-nowrap' },
			cell: ({ row }) => formatDayTime(row.original.deadLetteredAt),
		},
		{
			id: 'error',
			header: t('deadLetters.colError'),
			cell: ({ row }) => (
				<span className='block max-w-72 truncate text-muted-foreground'>{row.original.lastError ?? '—'}</span>
			),
		},
		{
			id: 'actions',
			header: '',
			meta: { cellClassName: 'w-px text-right' },
			cell: ({ row }) => (
				<Button
					size='sm'
					variant='outline'
					disabled={busy}
					onClick={(e) => {
						e.stopPropagation()
						void onReplay(row.original.eventId)
					}}
				>
					<RotateCcw />
					{t('deadLetters.replay')}
				</Button>
			),
		},
	]

	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		...WHOLE,
		getRowId: (r) => r.eventId,
	})

	return (
		<PageFrame
			title='deadLetters.title'
			actions={
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='outline' size='icon' aria-label={t('deadLetters.more')} data-testid='dead-letters-menu'>
							<MoreHorizontal />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end'>
						<DropdownMenuItem onSelect={() => setCutover(true)} data-testid='dead-letters-cutover'>
							{t('deadLetters.cutoverTitle')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			}
		>
			<Stats testId='dead-letters-stats'>
				<Stat
					label={
						<span className='inline-flex items-center gap-1'>
							{t('deadLetters.statWaiting')}
							<Hint>{t('deadLetters.subtitle')}</Hint>
						</span>
					}
					value={rows.length}
					tone='bad'
					valueTestId='dead-letters-count'
				/>
				<Stat label={t('deadLetters.statPartners')} value={partners} />
				<Stat label={t('deadLetters.statOldest')} value={oldest ? formatDayTime(oldest) : '—'} />
			</Stats>

			{outcome && <ToneNote tone={outcome.tone} title={outcome.text} testId='dead-letters-outcome' />}

			{query.isError ? (
				<p className='text-sm text-destructive'>{query.error.message}</p>
			) : query.isLoading ? (
				<p className='text-sm text-muted-foreground'>{t('deadLetters.loading')}</p>
			) : (
				<div data-testid='dead-letters-table'>
					<DataTable
						table={table}
						withPagination={false}
						emptyText={t('deadLetters.empty')}
						row={{
							attributes: (r) => ({ 'data-testid': `dead-letter-${r.eventId}` }),
							onRowClick: (r) => setOpenId(r.eventId),
						}}
					/>
				</div>
			)}

			<Sheet open={open !== null} onOpenChange={(o) => !o && setOpenId(null)}>
				<SheetContent className='overflow-y-auto data-[side=right]:sm:max-w-xl'>
					{open && <DeadLetterSheet row={open} busy={busy} onReplay={() => onReplay(open.eventId)} />}
				</SheetContent>
			</Sheet>

			<CutoverDialog open={cutover} onOpenChange={setCutover} onDone={() => query.refetch()} />
		</PageFrame>
	)
}

/** One dead letter in the side panel: what, to whom, about which Parcel, why it stopped, and the fact itself. */
function DeadLetterSheet({ row, busy, onReplay }: { row: DeadLetter; busy: boolean; onReplay: () => void }) {
	const { t } = useTranslate()
	const factName = useFactName()
	return (
		<div className='flex flex-col gap-6 pb-6' data-testid='dead-letter-sheet'>
			<SheetHeader className='pr-14 pb-0'>
				<div className='flex items-start gap-3'>
					<div className='flex min-w-0 flex-col gap-1'>
						<SheetTitle className='text-lg'>{factName(row.type)}</SheetTitle>
						<SheetDescription>
							{row.partnerName}
							{row.trackingCode ? ` · ${row.trackingCode}` : ''}
						</SheetDescription>
					</div>
					<Button size='sm' className='ml-auto' disabled={busy} onClick={onReplay}>
						<RotateCcw />
						{t('deadLetters.replay')}
					</Button>
				</div>
			</SheetHeader>
			<div className='flex flex-col gap-6 px-4'>
				<Facts>
					<Fact label={t('deadLetters.colParcel')}>
						<Button asChild variant='link' className='h-auto p-0 font-mono'>
							<Link to={`/parcels/${row.parcelId}`}>{row.trackingCode ?? t('deadLetters.openParcel')}</Link>
						</Button>
					</Fact>
					<Fact label={t('deadLetters.colAttempts')}>{row.attempts}</Fact>
					<Fact label={t('deadLetters.firstEnqueued')}>{formatDateTime(row.firstEnqueuedAt)}</Fact>
					<Fact label={t('deadLetters.colDeadLettered')}>{formatDateTime(row.deadLetteredAt)}</Fact>
				</Facts>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-muted-foreground'>{t('deadLetters.colError')}</span>
					<p className='text-sm break-words'>{row.lastError ?? '—'}</p>
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-muted-foreground'>{t('deadLetters.eventId')}</span>
					<code className='font-mono text-xs break-all'>{row.eventId}</code>
				</div>
				{row.payload && (
					<div className='flex flex-col gap-1'>
						<span className='text-xs text-muted-foreground'>{t('deadLetters.payload')}</span>
						<pre className='overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs'>{row.payload}</pre>
					</div>
				)}
			</div>
		</div>
	)
}

/**
 * The supervised first delivery (ticket 32): first make sure the partner's receiver is
 * up, then refresh the window, then run one pass and read its result — and only then
 * switch the cycle on.
 */
function CutoverDialog({
	open,
	onOpenChange,
	onDone,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onDone: () => void
}) {
	const { t } = useTranslate()
	const [busy, setBusy] = useState(false)
	const [outcome, setOutcome] = useState<Outcome | null>(null)

	const run = async (action: () => Promise<string>) => {
		setBusy(true)
		try {
			setOutcome({ tone: 'ready', text: await action() })
		} catch {
			setOutcome({ tone: 'refused', text: t('deadLetters.failed') })
		} finally {
			onDone()
			setBusy(false)
		}
	}

	const onRequeue = () =>
		run(async () => {
			const res = await trpcClient.outbox.RequeuePending.mutate({})
			return t('deadLetters.requeueDone').replace('{count}', String(res.moved))
		})

	const onDrainOnce = () =>
		run(async () => {
			const res = await trpcClient.outbox.DrainOnce.mutate({})
			return t('deadLetters.drainOnceDone')
				.replace('{attempted}', String(res.attempted))
				.replace('{delivered}', String(res.delivered))
				.replace('{failed}', String(res.failed))
				.replace('{deadLettered}', String(res.deadLettered))
		})

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (busy) return
				onOpenChange(o)
				if (!o) setOutcome(null)
			}}
		>
			<DialogContent className='sm:max-w-lg' data-testid='dead-letters-cutover-dialog'>
				<DialogHeader>
					<DialogTitle>{t('deadLetters.cutoverTitle')}</DialogTitle>
					<DialogDescription>{t('deadLetters.cutoverHint')}</DialogDescription>
				</DialogHeader>
				<div className='flex flex-col gap-3'>
					<CutoverStep step={1} label={t('deadLetters.requeue')} hint={t('deadLetters.requeueHint')} busy={busy} onRun={onRequeue} />
					<CutoverStep step={2} label={t('deadLetters.drainOnce')} hint={t('deadLetters.drainOnceHint')} busy={busy} onRun={onDrainOnce} />
				</div>
				{outcome && <ToneNote tone={outcome.tone} title={outcome.text} testId='dead-letters-cutover-outcome' />}
			</DialogContent>
		</Dialog>
	)
}

function CutoverStep({
	step,
	label,
	hint,
	busy,
	onRun,
}: {
	step: number
	label: string
	hint: string
	busy: boolean
	onRun: () => void
}) {
	return (
		<div className='flex items-center gap-3'>
			<Badge variant='outline' className='tabular-nums'>
				{step}
			</Badge>
			<Button variant='outline' disabled={busy} onClick={onRun}>
				{label}
			</Button>
			<Hint>{hint}</Hint>
		</div>
	)
}
